import React, { useEffect, useState, useRef } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Card } from '@sunwisesoftware/sunwise-ui';
import { debounce, isEqual, isNull } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CustomPagination from 'common/components/CustomPagination';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';
import CustomColumns from 'common/modules/customColumns';
import { getOrderedColumns, getHiddenColumns } from 'common/utils/helpers';

import { COLUMN_KEYS, REQUIRED_COLUMNS_KEYS } from '../constants';

import BulkOperationsCard from './BulkOperationsCard';
import Columns from './Columns';
import Filters from './Filters';

const ListItems = ({
    alliances,
    canModify,
    downloadFile,
    filterData,
    filterItems,
    handleClickBulkItems,
    handleClickChangeStatus,
    handleClickReassignAdvisor,
    handleSaveRow,
    isFetching,
    isFetchingAlliances,
    isOpenModalChangeStatus,
    isOpenModalReassingAdvisor,
    items,
    paginationData,
    setFilterData,
    statusList,
    tableSettings,
    users,
}) => {
    const { t } = useTranslation();
    const [rowCount, setRowCount] = useState(paginationData?.rowCount || 0);
    const [rowModesModel, setRowModesModel] = useState({});
    const [selectedBulkOption, setSelectedBulkOption] = useState('');
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectionModel, setSelectionModel] = useState([]);
    const [currentFilterData, setCurrentFilterData] = useState(filterData);

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleClickAlliance = (value) => {
        setFilterData({
            ...filterData,
            allianceId: value,
        });
    };

    const handleClickStatus = (value) => {
        setFilterData({
            ...filterData,
            status: value,
        });
    };

    const handleClickUser = (value) => {
        setFilterData({
            ...filterData,
            financialAdvisor: value,
        });
    };

    const handleOnChangeSearch = (event) => {
        setFilterData({
            ...filterData,
            page: 0,
            searchText: event.target.value,
        });
    };

    const handleOnSelectStatus = (value) => {
        const filter = { ...filterData, page: 0, status2: value };
        setFilterData(filter);
    };

    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));

    const handleOnSortModelChange = (sortModel) => {
        if (!sortModel[0]) return;

        setFilterData({
            ...filterData,
            orderBy: sortModel[0].field.toLocaleLowerCase(),
            sortBy: sortModel[0].sort,
        });
    };
    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;

    const onRowEditStart = (params, event) => {
        const id = params.row.id;
        if (isNull(selectedRowId)) setSelectedRowId(id);
        else event.defaultMuiPrevented = true;
        return false;
    };

    const onRowEditStop = (_, event) => {
        event.defaultMuiPrevented = true;
    };

    const onRowModesModelChange = (model) => setRowModesModel(model);

    const onRowSelectionModelChange = (model) => setSelectionModel(model);

    const processRowUpdate = (newRow) => {
        handleSaveRow(filterData, newRow);
        return newRow;
    };

    const initialState = {
        pagination: {
            paginationModel: { pageSize: TABLE_PAGE_SIZE_OPTIONS[0] },
        },
    };
    const columns = Columns({
        canModify,
        hiddenColumns: getHiddenColumns(tableSettings),
        orders: getOrderedColumns(tableSettings),
        rowModesModel,
        selectedRowId,
        setRowModesModel,
        setSelectedRowId,
        tableSettings,
    });

    const columnVisibilityModel = columns.reduce(
        (acc, column) => ({ ...acc, [column.field]: !column.hide }),
        {},
    );

    const prefix = t('{{count}} selected request', {
        count: selectionModel.length,
    });

    const getRowHeight = () => 'auto';

    const slotProps = {
        pagination: {
            customContent: (
                <CustomColumns.Container
                    columnKeys={COLUMN_KEYS}
                    tableKey="applications"
                    requiredColumnsKeys={REQUIRED_COLUMNS_KEYS}
                />
            ),
            pageSizeOptions,
        },
    };
    const slots = { pagination: CustomPagination };

    useEffect(() => {
        if (paginationData)
            setRowCount((prevRowCount) =>
                paginationData?.total_rows !== undefined
                    ? paginationData?.total_rows
                    : prevRowCount,
            );
    }, [paginationData?.total_rows, setRowCount]);

    useEffect(() => {
        if (!isEqual(currentFilterData, filterData)) {
            handleOnFilter(filterData);
            setCurrentFilterData(filterData);
        }
    }, [filterData]);

    useEffect(() => {
        if (!isOpenModalReassingAdvisor) setSelectedBulkOption('');
        if (!isOpenModalChangeStatus) setSelectedBulkOption('');
    }, [isOpenModalChangeStatus, isOpenModalReassingAdvisor]);

    return (
        <>
            <Filters
                alliances={alliances}
                downloadFile={downloadFile}
                filterData={filterData}
                handleClickAlliance={handleClickAlliance}
                handleOnChangeSearch={handleOnChangeSearch}
                handleOnSelectStatus={handleOnSelectStatus}
                handleClickUser={handleClickUser}
                handleClickStatus={handleClickStatus}
                isFetchingAlliances={isFetchingAlliances}
                selectionModel={selectionModel}
                statusList={statusList}
                users={users}
            />
            <Card>
                <Card.Body>
                    <BulkOperationsCard
                        canModify={canModify}
                        filterData={filterData}
                        handleClickBulkItems={handleClickBulkItems}
                        handleClickChangeStatus={handleClickChangeStatus}
                        handleClickReassignAdvisor={handleClickReassignAdvisor}
                        prefix={prefix}
                        selectedBulkOption={selectedBulkOption}
                        selectionModel={selectionModel}
                        setSelectedBulkOption={setSelectedBulkOption}
                        setSelectionModel={setSelectionModel}
                    />
                    <DataGrid
                        checkboxSelection={canModify}
                        columnVisibilityModel={columnVisibilityModel}
                        columns={columns}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        editMode="row"
                        getRowHeight={getRowHeight}
                        hideFooterSelectedRowCount
                        initialState={initialState}
                        loading={isFetching}
                        onPaginationModelChange={onPaginationModelChange}
                        onRowEditStart={onRowEditStart}
                        onRowEditStop={onRowEditStop}
                        onRowModesModelChange={onRowModesModelChange}
                        onRowSelectionModelChange={onRowSelectionModelChange}
                        onSortModelChange={handleOnSortModelChange}
                        pageSizeOptions={pageSizeOptions}
                        pagination
                        paginationMode="server"
                        processRowUpdate={processRowUpdate}
                        rowCount={rowCount}
                        rowModesModel={rowModesModel}
                        rows={items}
                        selectionModel={selectionModel}
                        slotProps={slotProps}
                        slots={slots}
                        sortingMode="server"
                    />
                </Card.Body>
            </Card>
        </>
    );
};

ListItems.propTypes = {
    alliances: PropTypes.array,
    canModify: PropTypes.bool,
    downloadFile: PropTypes.func,
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    handleClickChangeStatus: PropTypes.func,
    handleClickReassignAdvisor: PropTypes.func,
    handleSaveRow: PropTypes.func,
    isFetching: PropTypes.bool,
    isFetchingAlliances: PropTypes.bool,
    isOpenModalChangeStatus: PropTypes.bool,
    isOpenModalReassingAdvisor: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    setFilterData: PropTypes.func,
    statusList: PropTypes.array,
    tableSettings: PropTypes.array,
    users: PropTypes.array,
};

export default ListItems;
